import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/common.css";
import "../styling/ContentManagerAccess.css";
import { createLogger } from "../../utils/logger";
import { LoadingState } from "../Components/LoadingState";
import { LoginForm } from "../Components/LoginForm";
import { API_ENDPOINTS, DEFAULT_LIST_ID, ROUTES } from "../constants/contentManagerAccessConstants";
import { TIMING } from "../constants/timingConstants";
import {
    buildAuthorizationUrl,
    parseCallback,
    exchangeCodeForTokens,
    clearCallbackParams,
} from "./manualOAuth2";

const logger = createLogger("Frontend");

/**
 * Check if an error object has a message property
 * @param error
 * @returns boolean
 */
const isErrorWithMessage = (error: unknown): error is Error => {
    return typeof error === "object" && error !== null && "message" in error;
};

/**
 * Get the error message from an error object
 * @param error
 * @returns error message
 */
const getErrorMessage = (error: unknown): string => {
    if (isErrorWithMessage(error)) {
        return error.message;
    }
    return "Unknown error";
};

/**
 * Content Manager Access Component with Manual OAuth2
 */
export const ContentManagerAccess: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();
    const processingRef = React.useRef<string | null>(null);

    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    const redirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI ?? window.location.origin;

    /**
     * Handle OAuth2 callback on mount
     */
    useEffect(() => {
        const handleCallback = async () => {
            try {
                const callback = parseCallback();

                if (!callback) {
                    return;
                }

                // Prevent double processing of the same authorization code
                if (processingRef.current === callback.code) {
                    logger.info("Already processing this authorization code, skipping...");
                    return;
                }
                processingRef.current = callback.code;

                if (!domain || !clientId) {
                    throw new Error("Auth0 configuration missing");
                }

                logger.info("OAuth2 callback detected, exchanging code for token...");
                setIsAuthenticating(true);
                setError(null);

                // Exchange code for token
                const accessToken = await exchangeCodeForTokens(
                    domain,
                    clientId,
                    redirectUri,
                    callback.code,
                );

                logger.info("Got access token, sending to backend...");

                // Send token to backend for session creation
                const response = await fetch(API_ENDPOINTS.AUTH_LOGIN, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token: accessToken }),
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    logger.info("Backend session created, showing success state...");
                    clearCallbackParams();

                    // Show authenticated loading state
                    setIsAuthenticating(false);
                    setIsAuthenticated(true);

                    // Delay navigation to show "Setting up your session" message
                    setTimeout(() => {
                        logger.info("Navigating to content manager...");
                        void navigate(`${ROUTES.CONTENT_MANAGER}/${DEFAULT_LIST_ID}`);
                    }, TIMING.AUTH_SUCCESS_DELAY_MS);
                } else {
                    const errorMsg = data.error || "Failed to create session";

                    if (errorMsg.includes("not valid") || errorMsg.includes("companyId")) {
                        setError(
                            "Authentication token is missing required company ID. Please contact your administrator to configure the Auth0 custom claim: https://vimond/companyId",
                        );
                    } else {
                        setError(errorMsg);
                    }

                    logger.error("Backend session creation failed:", errorMsg);
                    clearCallbackParams();
                    setIsAuthenticating(false);
                }
            } catch (err: unknown) {
                logger.error("OAuth2 callback error:", err);
                setError(`Authentication failed: ${getErrorMessage(err)}`);
                clearCallbackParams();
                setIsAuthenticating(false);
            }
        };

        void handleCallback();
    }, [navigate, domain, clientId, redirectUri]);

    /**
     * Handle login button click
     */
    const handleLogin = async () => {
        setError(null);

        if (!domain || !clientId) {
            setError("Auth0 configuration is missing. Please check your .env file.");
            return;
        }

        try {
            logger.info("Building Auth0 authorization URL...");
            const authUrl = await buildAuthorizationUrl(domain, clientId, redirectUri, audience);

            logger.info("Redirecting to Auth0...");
            window.location.href = authUrl;
        } catch (err: unknown) {
            logger.error("Login error:", err);
            setError(`Login failed: ${getErrorMessage(err)}`);
        }
    };

    if (isAuthenticating) {
        return <LoadingState isAuthenticated={false} />;
    }

    if (isAuthenticated) {
        return <LoadingState isAuthenticated />;
    }

    return <LoginForm onLogin={handleLogin} error={error} />;
};
